import { compareVersion, Env, App } from '@voghion/fe-util';
import { isMicroApp } from './env.mjs';
/**
 * js bridge 版本定义
 */
const bridges = [
    {
        app: App.Voghion,
        version: [
            {
                ios: '1.0.0',
                android: '1.0.0',
                library: '1.0.0' // library version
            },
            {
                ios: '1.6.4',
                android: '1.8.0',
                library: '1.0.1'
            },
            {
                ios: '1.6.5',
                android: '1.8.1',
                library: '1.0.2'
            },
            {
                ios: '1.7.4',
                android: '1.8.7',
                library: '1.0.3'
            },
            {
                ios: '1.8.2',
                android: '1.9.7',
                library: '1.0.4'
            },
            {
                ios: '1.8.3',
                android: '1.9.8',
                library: '1.0.5'
            },
            {
                ios: '1.8.5',
                android: '1.9.9',
                library: '1.0.6'
            },
            {
                ios: '1.9.0',
                android: '2.0.3',
                library: '1.0.7'
            },
            {
                ios: '1.9.5',
                android: '2.0.8',
                library: '1.0.8'
            },
            {
                ios: '1.9.6',
                android: '2.0.9',
                library: '1.0.9'
            },
            {
                ios: '1.9.7',
                android: '2.1.3',
                library: '1.1.0'
            },
            {
                ios: '1.9.8',
                android: '2.1.4',
                library: '1.1.1'
            },
            {
                ios: '2.0.9',
                android: '2.2.3',
                library: '1.1.2'
            },
            {
                ios: '2.2.0',
                android: '2.3.0',
                library: '1.1.3'
            },
            {
                ios: '2.2.3',
                android: '2.3.0',
                library: '1.1.4'
            },
            {
                ios: '2.2.5',
                android: '2.3.8',
                library: '1.1.5'
            }
        ]
    },
    {
        app: App.MocasaShopping,
        version: [
            {
                ios: '1.0.0',
                android: '1.0.0',
                library: '1.0.2'
            }
        ]
    }
];
const minVersion = '1.0.0';
let microAppApiChecker;
/**
 * 通过外部指定微前端的 api 校验，避免循环引用
 * @param checker
 */
export const setMircoAppApiChecker = (checker) => {
    microAppApiChecker = checker;
};
/**
 * 检查库版本是否匹配
 * @param libraryVersion 库版本
 */
export function checkBridgeVersion(libraryVersion = minVersion) {
    const env = Env.getInstance();
    const appConfig = env.getAppConfig();
    const app = appConfig ? appConfig.app : bridges[0].app;
    const appVersion = appConfig ? appConfig.version || minVersion : minVersion;
    const bridge = bridges.filter((r) => r.app === app)[0] || bridges[0];
    let versionValid = !libraryVersion;
    for (let i = 0, len = bridge.version.length; i < len; i++) {
        const item = bridge.version[i];
        if (compareVersion(libraryVersion, item.library) <= 0) {
            if (env.isIOS) {
                versionValid = compareVersion(appVersion, item.ios) >= 0;
            }
            else if (env.isAndroid) {
                versionValid = compareVersion(appVersion, item.android) >= 0;
            }
            break;
        }
    }
    return versionValid;
}
export const defaultTimeout = 20000; // 默认超时时间
/**
 * 接口最小超时时间，该值不能过小
 * 1. 微前端是基于 iframe 实现的，js 是单线程，iframe 和 父窗口大概率都是用的同一个线程，一旦发生阻塞，postMessage 就有可能超时
 * 2. 客户端处理也有可能阻塞，例如刚好有其他异步任务在主线程处理
 */
const minTimeout = 1000;
export function formatCallOptions(options) {
    if (typeof options === 'object') {
        return options;
    }
    return {
        minVersion: options
    };
}
/**
 * 从环境配置中获取所需的最小库版本号
 * @param options
 * @returns
 */
function getMinLibraryVersion(options) {
    return (typeof options === 'string' ? options : options.minVersion) || '1.0.0';
}
/**
 * bridge 错误
 */
export class BridgeError extends Error {
    constructor(error) {
        const msg = typeof error === 'string' ? error : error.message || '';
        super(msg);
        this._isVersionInvalid = false;
        this._isEnvInvalid = false;
        this._isTimeout = false;
    }
    /**
     * 是否是版本不满足条件
     */
    set isVersionInvalid(value) {
        this._isVersionInvalid = value;
    }
    get isVersionInvalid() {
        return this._isVersionInvalid;
    }
    /**
     * 是否是环境不满足条件
     */
    set isEnvInvalid(value) {
        this._isEnvInvalid = value;
    }
    get isEnvInvalid() {
        return this._isEnvInvalid;
    }
    /**
     * 是否是超时
     */
    set isTimeout(value) {
        this._isTimeout = value;
    }
    get isTimeout() {
        return this._isTimeout;
    }
}
/**
 * 执行任务
 * @param options
 * @param task
 * @returns
 */
export function runTask(options, task) {
    return new Promise((resolve) => {
        let timer;
        let resolved = false;
        if (options.noResponse) {
            timer = setTimeout(() => {
                resolve({
                    data: {
                        mock: true
                    },
                    error: null
                });
            }, 50);
        }
        else if (options.timeout > 0) {
            const timeout = Math.max(options.timeout || defaultTimeout, minTimeout);
            timer = setTimeout(() => {
                resolved = true;
                const error = new BridgeError('timeout');
                error.isTimeout = true;
                resolve({
                    data: null,
                    error
                });
                timer = null;
            }, timeout);
        }
        task().then((data) => {
            if (resolved) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }
            resolve({
                data,
                error: null
            });
        }, (err) => {
            if (resolved) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }
            const error = new BridgeError(err);
            error.isEnvInvalid = true;
            resolve({
                data: null,
                error
            });
        });
    });
}
/**
 * 预检查包装器
 * @param options
 * @param task
 */
export function precheckWrapper(options, task) {
    return new Promise((resolve) => {
        const optionsObj = formatCallOptions(options);
        const env = Env.getInstance();
        if (isMicroApp() && !env.isApp() && microAppApiChecker) {
            // 微前端
            microAppApiChecker(optionsObj.apiName).then((valid) => {
                if (valid) {
                    if (optionsObj.checkMode) {
                        resolve({
                            data: null,
                            error: null
                        });
                    }
                    else {
                        runTask(optionsObj, task).then(resolve);
                    }
                }
                else {
                    const error = new BridgeError('api not exists');
                    error.isEnvInvalid = true;
                    resolve({
                        data: null,
                        error
                    });
                }
            });
        }
        else if (!env.isIOS && !env.isAndroid) {
            const error = new BridgeError('not in app');
            error.isEnvInvalid = true;
            resolve({
                data: null,
                error
            });
        }
        else {
            const libraryVersion = getMinLibraryVersion(optionsObj);
            if (checkBridgeVersion(libraryVersion)) {
                if (env.isIOS) {
                    if (optionsObj.iOSMinVersion &&
                        compareVersion(env.iOSVersion, optionsObj.iOSMinVersion) < 0) {
                        const error = new BridgeError(`iOS version invalid, minimum requirements ${optionsObj.iOSMinVersion}`);
                        error.isVersionInvalid = true;
                        resolve({
                            data: null,
                            error
                        });
                        return;
                    }
                }
                if (env.isAndroid) {
                    if (optionsObj.androidMinVersion &&
                        compareVersion(env.androidVersion, optionsObj.androidMinVersion) < 0) {
                        const error = new BridgeError(`android version invalid, minimum requirements ${optionsObj.androidMinVersion}`);
                        error.isVersionInvalid = true;
                        resolve({
                            data: null,
                            error
                        });
                        return;
                    }
                    if (optionsObj.androidChromeMinVersion) {
                        const match = navigator.userAgent
                            .toLowerCase()
                            .match(/chrome\/([\d.]+)/i);
                        const chromeVersion = match ? match[1] : '';
                        if (compareVersion(chromeVersion, optionsObj.androidChromeMinVersion) < 0) {
                            const error = new BridgeError(`android chrome version invalid, minimum requirements ${optionsObj.androidChromeMinVersion}`);
                            error.isVersionInvalid = true;
                            resolve({
                                data: null,
                                error
                            });
                            return;
                        }
                    }
                }
                if (optionsObj.checkMode) {
                    resolve({
                        data: null,
                        error: null
                    });
                }
                else {
                    runTask(optionsObj, task).then(resolve);
                }
            }
            else {
                const error = new BridgeError('app version invalid');
                error.isVersionInvalid = true;
                resolve({
                    data: null,
                    error
                });
            }
        }
    });
}
